/**
 * @file Bandeau d'avertissement compact placé dans le flux d'un formulaire.
 * @module packages.common.components.inline-notice.InlineNotice
 *
 * Rendu unique des avertissements de saisie (allergies non détaillées, domaine
 * email douteux) : fond teinté léger, icône et texte dans la teinte foncée du
 * thème. Plus discret qu'une `Alert` MUI, il accompagne un champ sans prendre
 * le pas sur le formulaire. Une action (bouton) et une fermeture sont optionnelles.
 */

import React from 'react';
import PropTypes from 'prop-types';
import {alpha} from '@mui/material/styles';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@mui/icons-material/Close';

/**
 * Affiche un avertissement compact sur toute la largeur de son conteneur.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Message affiché
 * @param {React.ReactNode} [props.action] - Action proposée sous le message (bouton texte)
 * @param {Function} [props.onClose] - Affiche une croix de fermeture si fourni
 * @param {string} [props.closeLabel] - Libellé accessible de la croix
 * @param {Object} [props.sx] - Styles complémentaires (marges)
 * @return {JSX.Element} Bandeau d'avertissement
 */
export default function InlineNotice({children, action, onClose, closeLabel = 'Fermer', sx, ...other}) {
  return (
    <Box
      role="status"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        py: 1,
        borderRadius: 1,
        backgroundColor: (theme) => alpha(theme.palette.warning.main, 0.1),
        ...sx,
      }}
      {...other}
    >
      <WarningAmberIcon sx={{fontSize: 18, color: 'warning.dark', flexShrink: 0}} aria-hidden="true" />
      <Box sx={{flex: 1, minWidth: 0}}>
        <Typography variant="body2" sx={{color: 'warning.dark'}}>
          {children}
        </Typography>
        {action}
      </Box>
      {onClose && (
        <IconButton size="small" onClick={onClose} aria-label={closeLabel} sx={{color: 'warning.dark', flexShrink: 0}}>
          <CloseIcon sx={{fontSize: 18}} />
        </IconButton>
      )}
    </Box>
  );
}

InlineNotice.propTypes = {
  children: PropTypes.node.isRequired,
  action: PropTypes.node,
  onClose: PropTypes.func,
  closeLabel: PropTypes.string,
  sx: PropTypes.object,
};
